import React from "react";
import copy from "copy-to-clipboard";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { Button, Label, Spinner, Tooltip } from "@/components/ui";
import { highlightHtmlSnippet } from "./highlightHtmlSnippet";
import { EMBED_IFRAME_HEIGHT, MOBILE_PREVIEW_WIDTH, PreviewDevice } from "./useReviewWidgetPreview";

interface Props {
  iframeRef: React.RefObject<HTMLIFrameElement>;
  iframeSrc: string;
  includeAutoResize: boolean;
  iframeHeight: number;
  previewDevice: PreviewDevice;
  isLoading: boolean;
  previewPlayerName?: string;
  iframeSnippet: string;
}

const ReviewWidgetPreview: React.FC<Props> = ({
  iframeRef,
  iframeSrc,
  includeAutoResize,
  iframeHeight,
  previewDevice,
  isLoading,
  previewPlayerName,
  iframeSnippet,
}) => {
  const handleCopySnippet = () => {
    if (!iframeSnippet) {
      toast.error("Embed URL is not available yet");
      return;
    }
    copy(iframeSnippet);
    toast.success("Iframe snippet copied");
  };

  const isMobilePreview = previewDevice === "mobile";
  const previewHeight = includeAutoResize ? iframeHeight : EMBED_IFRAME_HEIGHT;

  return (
    <div className="min-w-0 w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
      <div className="relative min-w-0 w-full">
        <iframe
          key={iframeSrc}
          ref={iframeRef}
          title="Review Widget preview"
          src={iframeSrc}
          className={`block border-0 bg-white ${isMobilePreview ? "mx-auto" : "w-full"}`}
          style={{
            height: previewHeight,
            ...(isMobilePreview ? { width: MOBILE_PREVIEW_WIDTH, maxWidth: "100%" } : undefined),
          }}
        />
        {isLoading && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-white">
            <Spinner className="size-6 text-slate-500" />
            <p className="text-sm text-slate-500">Loading {previewPlayerName || "player"} preview…</p>
          </div>
        )}
      </div>

      <div className="min-w-0 border-t border-slate-200">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-100 px-3 py-2">
          <Label className="text-slate-600">Iframe snippet</Label>
          <Tooltip>
            <Tooltip.Trigger
              render={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
                  onClick={handleCopySnippet}
                  aria-label="Copy snippet"
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              }
            />
            <Tooltip.Content>Copy snippet</Tooltip.Content>
          </Tooltip>
        </div>
        <pre className="m-0 overflow-x-auto whitespace-pre p-4 font-mono text-[14px] leading-6 selection:bg-slate-300/60">
          {highlightHtmlSnippet(iframeSnippet)}
        </pre>
      </div>
    </div>
  );
};

export { ReviewWidgetPreview };
